<template>
  <div>
    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Cargos de Funcionários</h5>
      <b-button
        v-if="hasPermission('adicionar_cargos_funcionarios')"
        variant="primary"
        class="d-flex align-items-center"
        @click="irParaCadastro"
      >
        <i class="bi bi-plus-circle-fill" title="Novo Cargo"></i>
      </b-button>
    </div>

    <!-- Filtros -->
    <div class="d-flex mb-3" style="gap: 10px;">
      <input
        v-model="filters.nome"
        placeholder="Filtrar por nome"
        class="form-control"
        @input="filtrarCargos"
      />
      <b-button
        variant="secondary"
        @click="limparFiltros"
        class="d-flex align-items-center"
      >
        <i class="bi bi-funnel" title="Limpar Filtros"></i>
      </b-button>
    </div>

    <!-- Tabela customizada -->
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th>
            <button class="btn btn-link p-0" @click="sort('nome')">
              Nome {{ arrow('nome') }}
            </button>
          </th>
          <th class="text-end">Ações</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in cargos" :key="row.id">
          <td>{{ row.nome }}</td>
          <td class="text-end">
            <i
              v-if="hasPermission('detalhar_cargos_funcionarios')"
              class="bi bi-eye-fill text-primary mx-2"
              title="Detalhar"
              @click="detalharCargo(row.id)"
            ></i>
            <i
              v-if="hasPermission('alterar_cargos_funcionarios')"
              :class="[
                'bi bi-pencil-fill mx-2',
                isProtegido(row.nome) ? 'text-secondary disabled' : 'text-warning'
              ]"
              :title="isProtegido(row.nome) ? 'Não é possível alterar' : 'Editar'"
              @click.prevent="!isProtegido(row.nome) && editarCargo(row.id)"
            ></i>
            <i
              v-if="hasPermission('excluir_cargos_funcionarios')"
              :class="[
                'bi bi-trash-fill mx-2',
                isProtegido(row.nome) ? 'text-secondary disabled' : 'text-danger'
              ]"
              :title="isProtegido(row.nome) ? 'Não é possível excluir' : 'Excluir'"
              @click.prevent="!isProtegido(row.nome) && excluirCargo(row.id)"
            ></i>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Paginação -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <!-- 1. Resumo -->
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ totalItems }} registros
      </div>

      <!-- 2. Seletor de itens por página -->
      <div class="d-flex align-items-center" style="gap: .5rem;">
        <b-form-select
          v-model="limitePorPagina"
          :options="pageOptions.map(n => ({ value: n, text: n + ' / página' }))"
          size="sm"
          style="width: 6rem"
          @change="onChangeLimit"
        />
      </div>

      <!-- 3. Controle de página -->
      <div class="d-flex align-items-center">
        <b-pagination
          v-model="paginaAtual"
          :per-page="limitePorPagina"
          :total-rows="totalItems"
          size="sm"
          limit="5"
          @input="onPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, getCurrentInstance, computed, watch, ref } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/services/axios'
import { BButton, BPagination, BFormSelect } from 'bootstrap-vue-next'
import { useSortableTable } from '@/utils/useSortableTable'

const router = useRouter()
const { proxy } = getCurrentInstance()

// composable para cargos de funcionários
const {
  items: cargos,
  totalItems,
  page: paginaAtual,
  limit: limitePorPagina,
  filters,
  load,
  sort,
  arrow
} = useSortableTable(
  `/cadastros/cargos-funcionarios`,
  { key: 'nome', order: 'asc' }
)

// opções de limite
const pageOptions = ref([10, 15, 20, 25, 50])

// intervalos para o resumo
const startRow = computed(() =>
  (paginaAtual.value - 1) * limitePorPagina.value + 1
)
const endRow = computed(() =>
  Math.min(paginaAtual.value * limitePorPagina.value, totalItems.value)
)

// watchers para recarregar ao mudar página ou limite
watch(paginaAtual, () => load())
watch(limitePorPagina, () => {
  paginaAtual.value = 1
  load()
})

// inicialização
onMounted(() => {
  filters.value = { nome: '' }
  load()
})

// handlers de filtro
function filtrarCargos() {
  paginaAtual.value = 1
  load()
}
function limparFiltros() {
  filters.value.nome = ''
  paginaAtual.value = 1
  load()
}
function onChangeLimit() {
  paginaAtual.value = 1
  load()
}
function onPageChange() {
  load()
}

// navegação
function irParaCadastro() {
  router.push('/cadastros/cargos-funcionarios/novo')
}
function editarCargo(id) {
  router.push(`/cadastros/cargos-funcionarios/${id}`)
}
function detalharCargo(id) {
  router.push(`/cadastros/cargos-funcionarios/${id}/detalhar`)
}

// exclusão
async function excluirCargo(id) {
  const confirmou = await proxy.$confirm(
    'Tem certeza que deseja excluir este cargo?',
    'Confirmação'
  )
  if (!confirmou) return
  try {
    await axios.delete(`/cadastros/cargos-funcionarios/${id}`)
    load()
    proxy.$toast.success('Cargo excluído com sucesso!')
  } catch (e) {
    const msg = e.response?.data?.error || 'Erro ao excluir cargo.'
    await proxy.$alert(msg, 'Erro')
  }
}

const isProtegido = (nome) => {
  return nome === 'Instrutor' || nome === 'Instrutor - Supervisor'
}


</script>

<style scoped>
:deep(.pagination.pagination-sm .page-link) {
  padding: rem rem;
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}
</style>
